/* External */
import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  NavLink, Link, useLocation, useParams,
} from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import isEmptyL from 'lodash-es/isEmpty';
import getL from 'lodash-es/get';

/* Ott-common */
import { getInitials } from '@dtvgo/utils';
import CardCreateProfile from '@dtvgo/card-create-profile';
import { gtmEvent } from '@dtvgo/gtm-event-report';

/* Assets */
import logo from '@/assets/theme/svg/logo.svg';

/* Components */
import UserMenu from '../userMenu/UserMenu';
import InputSearch from './InputSearch';
import { useGtmContext } from '../components/GtmContext';
import { gtmDimensions } from '../components/GtmContext/dimensions/header-footer';

/* Styles */
import './NavBar.scss';

/* Other */
import {
  PATHS,
  PROFILES_STORAGE,
  PUBLIC_PATHS,
  alcatrazFeatureFlag,
  colors,
  formatContentForGtm,
  getEncryptedData,
  getLanderNameByCaption,
  isBrazil,
} from '../utils';
import { getImage } from '../theme/images';

function NavBar({
  user = null,
  notificationHeight,
  userCountry,
}) {
  const [menu, setMenu] = useState(false);
  const {
    gtmUserData,
    gtmCollectionName,
    gtmProviderName,
    setGtmCollectionName,
    setGtmProviderName,
  } = useGtmContext();
  const location = useLocation();
  const { country } = useParams() || {};

  const handleClick = (caption) => {
    const { hash, eventType, dimensions } = gtmDimensions[caption].watch;

    const dataTosend = formatContentForGtm(
      {},
      {
        tab: location.state?.tab || null,
        providerName: gtmProviderName,
        collectionName: gtmCollectionName,
      },
    );

    gtmEvent({
      hash,
      eventType,
      dimensions,
      userData: gtmUserData,
      data: dataTosend,
    });

    setGtmCollectionName(null);
    setGtmProviderName(null);
  };

  const showUserMenu = () => {
    setMenu(!menu);
  };

  let captionLink;
  let userName = null;

  let profilesData;

  if (alcatrazFeatureFlag) {
    const decrypted = getEncryptedData(PROFILES_STORAGE.profiles);
    profilesData = typeof decrypted === 'string' ? JSON.parse(decrypted) : decrypted;
  } else {
    profilesData = JSON.parse(localStorage.getItem(PROFILES_STORAGE.profiles));
  }

  const profileSelected = alcatrazFeatureFlag
    ? getEncryptedData(PROFILES_STORAGE.profile) ?? []
    : JSON.parse(localStorage.getItem(PROFILES_STORAGE.profile)) ?? [];

  const profileIndex = profilesData.findIndex(
    (profileData) => profileData.id === profileSelected?.id,
  );

  const profileName = profileSelected?.name ? profileSelected?.name : '';
  const profileColor = profileSelected?.color ? profileSelected?.color : profileIndex;
  const logoLetter = useMemo(() => getInitials(profileName).slice(0, 2).join('').toUpperCase(), [profileName]);
  const storeCode = (userCountry || country || '').toLowerCase();

  const getLinkUrl = (url) => (storeCode && !isBrazil ? `/${storeCode}${url}` : url);
  let menuLinks = [
    {
      caption: 'plans',
      default: 'Planes',
      url: getLinkUrl(PUBLIC_PATHS.PLANS),
    },
    {
      caption: 'extras',
      default: 'Extras',
      url: getLinkUrl(PUBLIC_PATHS.EXTRAS),
    },
    {
      caption: 'live',
      default: 'En vivo',
      url: getLinkUrl(PUBLIC_PATHS.LIVE),
    },
    {
      caption: 'sports',
      default: 'Deportes',
      url: getLinkUrl(PUBLIC_PATHS.SPORTS),
    },
    {
      caption: 'help',
      default: 'Ayuda',
      url: getLinkUrl(PUBLIC_PATHS.HELP),
    },
  ];

  if (!isEmptyL(user)) {
    userName = user.userName ? user.userName.toLowerCase() : user.profile.email;
  }

  if (userName) {
    menuLinks = [
      {
        caption: 'home',
        default: 'Inicio',
        url: PATHS.HOME,
      },
      {
        caption: 'live',
        default: 'En vivo',
        url: PATHS.LIVE,
      },
      {
        caption: 'watch',
        default: 'Catálogo',
        url: PATHS.WATCH,
      },
      {
        caption: 'sports',
        default: 'Deportes',
        url: PATHS.SPORTS,
      },
      {
        caption: 'kids',
        default: 'Niños',
        url: PATHS.KIDS,
      },
    ];
  }

  return (
    <>
      <div className={`dtv-nav-bar-container ${!userName && 'dtv-public-menu'}`} style={{ top: notificationHeight }}>
        <div className="dtv-nav-bar-content">
          <div className="dtv-nav-bar-content-left">
            <Link to={userName ? PATHS.HOME : '/'} className="dtv-logo">
              <img src={getImage('logo') || logo} alt="Logo" />
            </Link>
            <div className="dtv-navlinks">
              {menuLinks.map((menuLink) => {
                captionLink = <FormattedMessage id={`menu.${menuLink.caption}`} defaultMessage={menuLink.default} />;
                return (
                  <NavLink
                    to={{
                      pathname: menuLink.url,
                      state: { tab: getLanderNameByCaption(menuLink.caption) },
                    }}
                    key={menuLink.caption}
                    className="dtv-navlink-item"
                    onClick={() => handleClick(menuLink.caption)}
                  >
                    {captionLink}
                  </NavLink>
                );
              })}
            </div>
          </div>

          {userName && (
            <div className="dtv-nav-bar-content-right">
              <InputSearch />
              <button id="lblUserName" type="button" onClick={() => showUserMenu()} className="dtv-resemble-a">
                <CardCreateProfile
                  isInputNotEmpty={logoLetter || ''}
                  color={colors[profileColor + 1]}
                />
              </button>
            </div>
          )}
        </div>
      </div>
      {userName && <UserMenu menu={menu} setMenu={setMenu} />}
    </>
  );
}

function mapStateToProps({
  user, notifications, router, global,
}) {
  const { userCountry } = getL(global, 'configurationAEM.data', '');
  const barNotification = notifications.get('barNotification');
  const notificationHeight = notifications.get('notificationHeight');
  const { pathname } = router.location;

  return {
    user: user.account,
    barNotification,
    notificationHeight,
    pathname,
    userCountry,
  };
}

export default connect(mapStateToProps)(injectIntl(NavBar));
